<script setup>
const props = defineProps({
  title: {
    type: String,
    default: undefined,
  },

  href: {
    type: String,
    default: undefined,
  },
  hrefs: {
    type: Array,
    default: () => [],
  },

  image: {
    type: Object,
    default: undefined,
  },

  modelValue: {
    type: String,
    default: undefined,
  },
});
const emit = defineEmits(['update:modelValue', 'redirect']);

const { t } = useI18n();

const imageSrc = computed(
  () => props.image?.original || '/images/seo/default/featured-large.png',
);

const sortedHrefs = computed(() => {
  return [
    ...props.hrefs.filter((item) => item.type === 'routes'),
    ...props.hrefs.filter((item) => item.type !== 'routes'),
  ];
});
</script>

<template>
  <div class="bundles-trains-navigation-columns-page-types">
    <div class="bundles-trains-navigation-columns-page-types__pages">
      <button
        v-for="(item, index) in sortedHrefs"
        :key="index"
        type="button"
        class="bundles-trains-navigation-columns-page-types__page"
        :class="{
          'bundles-trains-navigation-columns-page-types__page--active':
            modelValue === item.type,
        }"
        @click="emit('update:modelValue', item.type)"
      >
        <span
          class="bundles-trains-navigation-columns-page-types__font bundles-trains-navigation-columns-page-types__font--type"
        >
          {{ t(`wiki.pages.titles.${item.type}`, { country: title }) }}
        </span>

        <CIcon
          v-if="modelValue === item.type"
          class="bundles-trains-navigation-columns-page-types__icon"
          name="chevron-right"
        />
      </button>
    </div>

    <a
      class="bundles-trains-navigation-columns-page-types__link"
      :href="href"
      @click="emit('redirect', href)"
    >
      <span
        class="bundles-trains-navigation-columns-page-types__font bundles-trains-navigation-columns-page-types__font--link"
      >
        {{ t('wiki.pages.aboutCountry', { country: title }) }}
      </span>

      <CImage
        :key="imageSrc"
        class="bundles-trains-navigation-columns-page-types__image"
        :src="imageSrc"
        loading="lazy"
        :size="500"
        alt="Country image"
      />
    </a>
  </div>
</template>

<style scoped lang="scss">
.bundles-trains-navigation-columns-page-types {
  $parent: &;

  display: flex;
  flex-direction: column;
  overflow: hidden;
  background-color: $color-gray-10;
  border-radius: em(8);

  &__font {
    &--type {
      font-size: em(24);
    }

    &--link {
      position: relative;
      z-index: 3;
      font-size: em(24);
      font-weight: 600;
    }
  }

  &__icon {
    @include box(em(16));

    flex-shrink: 0;
    margin-left: auto;
    color: $color-black;
  }

  &__pages {
    display: flex;
    flex-direction: column;
    padding: em(18) em(40) em(20);
  }

  &__page {
    display: flex;
    flex-shrink: 0;
    gap: em(20);
    align-items: center;
    padding: em(17) 0 em(18);
    color: $color-main-5;
    transition: color $time-3;

    &:not(:last-child) {
      border-bottom: 1px solid rgba($color-black, 0.1);
    }

    @include hover {
      color: $color-second-1;
    }

    &--active {
      color: $color-second-1;

      #{$parent} {
        &__font {
          &--type {
            font-weight: 600;
          }
        }
      }
    }
  }

  &__link {
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    width: 100%;
    height: em(289);
    padding: em(14) em(40) em(30);
    margin-top: auto;
    color: $color-white;

    &::after {
      position: absolute;
      inset: 0;
      z-index: 2;
      content: '';
      background: linear-gradient(
        -315deg,
        rgb(112 0 255 / 100%) 0%,
        rgb(112 0 255 / 0%) 100%
      );
    }
  }

  &__image {
    @include box(100%);

    position: absolute;
    inset: 0;
    z-index: 1;
    object-fit: cover;
  }
}
</style>
